import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Settings, Shield, Database, FileText, UserCheck, Package, Eye, Users, Mail, Bell } from 'lucide-react'
import pushService from '../../utils/pushNotifications'
import api from '@/services/api'

const AdminDashboard = () => {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [stats, setStats] = useState({
    userCount: null,
    assetCount: null,
    documentCount: null,
    auditLogCount: null,
    systemHealth: null,
  });

  useEffect(() => {
    // Check push notification support and subscription status
    const checkPushStatus = async () => {
      setPushSupported(pushService.isSupported);
      if (pushService.isSupported) {
        const subscribed = await pushService.isSubscribed();
        setPushEnabled(subscribed);
      }
    };

    checkPushStatus();
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [dashboardRes, documentsRes, auditLogsRes] = await Promise.all([
          api.get('/admin/dashboard'),
          api.get('/admin/documents', { params: { limit: 1 } }),
          api.get('/admin/audit-logs', { params: { limit: 1 } }).catch(() => null),
        ]);

        setStats({
          userCount: dashboardRes.data?.data?.userStats?.total ?? null,
          assetCount: dashboardRes.data?.data?.assetStats?.total ?? null,
          systemHealth: dashboardRes.data?.data?.systemHealth?.status ?? null,
          documentCount: documentsRes.data?.data?.pagination?.totalItems ?? null,
          auditLogCount: auditLogsRes?.data?.data?.pagination?.totalItems ?? null,
        });
      } catch (error) {
        console.error('Error loading admin dashboard stats:', error);
      }
    };

    loadStats();
  }, []);

  const enablePushNotifications = async () => {
    try {
      const success = await pushService.subscribe();
      if (success) {
        setPushEnabled(true);
        alert('✅ Push notifications enabled successfully!');
      } else {
        alert('❌ Failed to enable push notifications');
      }
    } catch (error) {
      alert('❌ Error: ' + error.message);
    }
  };

  const testPushNotification = async () => {
    try {
      const success = await pushService.testPush('gmd');
      if (success) {
        alert('✅ Test push notification sent!');
      } else {
        alert('❌ Failed to send test push notification');
      }
    } catch (error) {
      alert('❌ Error: ' + error.message);
    }
  };

  const sendTestEmail = async () => {
    try {
      const response = await api.post('/test/send-test-email');
      const result = response.data;
      if (result.success) {
        alert('✅ Test email sent successfully to gmd@puffingroupltd.com!');
      } else {
        alert('❌ ' + result.message);
      }
    } catch (error) {
      alert('❌ Error: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Administration</h1>
        <p className="text-gray-600">Manage system settings, assets, and administrative tasks</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Settings className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">System Health</p>
              <p className="text-2xl font-semibold text-green-600 capitalize">
                {stats.systemHealth || 'Loading...'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Shield className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Security Score</p>
              <p className="text-2xl font-semibold text-gray-900">95%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Database className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Storage Used</p>
              <p className="text-2xl font-semibold text-gray-900">2.1GB</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Documents</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.documentCount != null ? stats.documentCount.toLocaleString() : '...'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Link
          to="/admin/users"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow border-2 border-blue-200 bg-blue-50"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-600" />
                User Management
              </h3>
              <p className="text-gray-600 mt-1">Manage users and their access levels</p>
              <div className="mt-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  ⭐ Featured
                </span>
              </div>
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {stats.userCount != null ? stats.userCount : '...'}
            </div>
          </div>
        </Link>



        <Link
          to="/admin/assets"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Package className="h-5 w-5 mr-2 text-purple-600" />
                Asset Management
              </h3>
              <p className="text-gray-600 mt-1">Track and manage company assets</p>
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {stats.assetCount != null ? stats.assetCount : '...'}
            </div>
          </div>
        </Link>

        <Link
          to="/admin/documents"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-yellow-600" />
                Document Repository
              </h3>
              <p className="text-gray-600 mt-1">Centralized document management</p>
            </div>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.documentCount != null ? stats.documentCount.toLocaleString() : '...'}
            </div>
          </div>
        </Link>

        <Link
          to="/admin/settings"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-gray-600" />
                System Settings
              </h3>
              <p className="text-gray-600 mt-1">Configure system preferences</p>
            </div>
            <div className="text-2xl font-bold text-gray-600">24</div>
          </div>
        </Link>

        <Link
          to="/admin/audit-logs"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Eye className="h-5 w-5 mr-2 text-indigo-600" />
                Audit Logs
              </h3>
              <p className="text-gray-600 mt-1">Monitor system activities</p>
            </div>
            <div className="text-2xl font-bold text-indigo-600">
              {stats.auditLogCount != null ? stats.auditLogCount.toLocaleString() : '...'}
            </div>
          </div>
        </Link>
      </div>

      {/* Feature Highlight */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">🎉 Enhanced: User Management</h3>
            <p className="text-blue-800 mb-4">
              Comprehensive user management with role-based access control. Create custom user types, manage permissions, 
              and control access to different modules. Full edit and delete capabilities included.
            </p>
            <div className="space-y-2 text-sm text-blue-700">
              <div>✅ Create, edit, and delete user types with specific permissions</div>
              <div>✅ Assign users to different roles (HR, Finance, Project Manager, etc.)</div>
              <div>✅ Control module access (Dashboard, Projects, Inventory, HR, Finance, etc.)</div>
              <div>✅ Self Service module available to all users</div>
              <div>✅ Super admin (admin@puffingroup.com) has full access</div>
            </div>
          </div>
          <Link
            to="/admin/users"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Users className="h-4 w-4" />
            Get Started
          </Link>
        </div>
      </div>

      {/* Email & Push Notifications Testing Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              📧 GMD Notifications
            </h3>
            <p className="text-purple-800 mb-4">
              Test email notifications and push notifications sent to GMD for purchase requests and transactions.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex gap-3">
              <button
                onClick={() => window.open('/email-preview', '_blank')}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
              >
                👁️ Preview Email
              </button>
              <button
                onClick={sendTestEmail}
                className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
              >
                📧 Send Test Email
              </button>
            </div>
            
            {/* Push Notification Controls */}
            {pushSupported && (
              <div className="flex gap-3">
                {!pushEnabled ? (
                  <button
                    onClick={enablePushNotifications}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <Bell className="h-4 w-4" />
                    Enable Push Notifications
                  </button>
                ) : (
                  <button
                    onClick={testPushNotification}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    🔔 Test Push Notification
                  </button>
                )}
              </div>
            )}
            
            {!pushSupported && (
              <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                Push notifications not supported in this browser
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard